import { useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ArrowDownIcon, ArrowUpIcon, ArrowsUpDownIcon } from '@heroicons/react/24/solid'
import { DataTableHeaderCell } from './DataTableCell'
import type { ColumnDefinition, DataTableItem, DataTableSort, SelectionConfig } from './types'

interface DataTableHeaderProps<T extends DataTableItem> {
  columns: ColumnDefinition<T>[]
  visibleColumns: string[]
  selection?: SelectionConfig<T>
  dataLength: number
  isScrolledX?: boolean
  onColumnSettingsClick?: () => void
  hasActions?: boolean
  resizable?: boolean
  columnWidths?: Record<string, number>
  resizingColumnId?: string | null
  onResizeStart?: (columnId: string, startX: number, target: HTMLElement) => void
  hasSpacerColumn?: boolean
  sort?: DataTableSort | null
  onSortChange?: (columnId: string) => void
}

export function DataTableHeader<T extends DataTableItem>({
  columns,
  visibleColumns,
  selection,
  dataLength,
  isScrolledX = false,
  onColumnSettingsClick,
  hasActions = false,
  resizable = false,
  resizingColumnId,
  onResizeStart,
  hasSpacerColumn = false,
  sort = null,
  onSortChange
}: DataTableHeaderProps<T>) {
  const allSelected = selection && selection.selectedIds.size > 0 && selection.selectedIds.size === dataLength
  const headerRef = useRef<HTMLTableSectionElement>(null)

  const handleResizeStart = useCallback((columnId: string) => (e: React.MouseEvent) => {
    if (!onResizeStart) return
    e.preventDefault()
    onResizeStart(columnId, e.clientX, e.currentTarget as HTMLElement)
  }, [onResizeStart])

  return (
    <thead ref={headerRef} className="bg-table-header sticky top-0 z-50">
      <tr className="text-left text-sm">
        {columns
          .filter(col => visibleColumns.includes(col.id))
          .map(column => {
            const isSticky = column.sticky === true
            const isSelectColumn = column.id === 'select' || column.id === 'name'
            const isResizing = resizingColumnId === column.id

            const isColumnResizable = resizable && column.resizable !== false

            if (isSelectColumn && selection) {
              return (
                <DataTableHeaderCell
                  key={column.id}
                  columnId={column.id}
                  isSticky={isSticky}
                  isScrolledX={isScrolledX}
                  className="pl-checkbox-cell pr-4"
                  resizable={isColumnResizable}
                  isResizing={isResizing}
                  onResizeStart={handleResizeStart(column.id)}
                >
                  <div className="flex items-center gap-4">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={() => {
                        if (allSelected) {
                          selection.onClearSelection()
                        } else {
                          selection.onSelectAll()
                        }
                      }}
                    />
                    {column.label && (
                      <span className="font-normal whitespace-nowrap overflow-hidden text-ellipsis">{column.label}</span>
                    )}
                  </div>
                </DataTableHeaderCell>
              )
            }

            return (
              <DataTableHeaderCell
                key={column.id}
                columnId={column.id}
                isSticky={isSticky}
                isScrolledX={isScrolledX}
                resizable={isColumnResizable}
                isResizing={isResizing}
                onResizeStart={handleResizeStart(column.id)}
              >
                {column.sortable && onSortChange ? (
                  <button
                    type="button"
                    onClick={() => onSortChange(column.id)}
                    className="flex items-center gap-1.5 max-w-full font-normal text-left cursor-pointer hover:text-foreground"
                    title={`Sort by ${column.label}`}
                  >
                    <span className="whitespace-nowrap overflow-hidden text-ellipsis">{column.label}</span>
                    {sort?.columnId === column.id
                      ? (sort.direction === 'asc'
                          ? <ArrowUpIcon className="h-3.5 w-3.5 shrink-0" />
                          : <ArrowDownIcon className="h-3.5 w-3.5 shrink-0" />)
                      : <ArrowsUpDownIcon className="h-4 w-4 shrink-0 opacity-50" />}
                  </button>
                ) : (
                  <span className="font-normal whitespace-nowrap overflow-hidden text-ellipsis">{column.label}</span>
                )}
              </DataTableHeaderCell>
            )
          })}

        {(hasActions || onColumnSettingsClick) && (
          <th className="px-4 py-1 font-normal bg-table-header sticky top-0 z-40 relative">
            {onColumnSettingsClick && (
              <Button
                variant="secondary"
                size="xs"
                onClick={onColumnSettingsClick}
                className="-ml-1 px-3 h-7 shadow-xl transform-gpu"
              >
                <p className="text-xs text-foreground/70">Edit Columns</p>
              </Button>
            )}
            <div className="absolute bottom-0 left-0 w-full h-px bg-table-grid" />
            <div className="absolute top-0 right-0 h-full bg-table-grid" style={{ width: '1px' }} />
          </th>
        )}

        {hasSpacerColumn && (
          <th className="bg-table-header sticky top-0 z-40 relative">
            <div className="absolute bottom-0 left-0 w-full h-px bg-table-grid" />
          </th>
        )}
      </tr>
    </thead>
  )
}
